/**
 * Turn a gist chip's text into the gist it names, and that gist into the page a
 * reader can open on the deployment's GitHub host.
 *
 * The chip text comes from {@link ./shell-summary.ts}, which passes a `gh gist`
 * operand (or a fetched URL) through as written — `89d7abf…`, `octocat/89d7abf…`,
 * `https://gist.github.com/octocat/89d7abf…`. This module walks that text back
 * into structure, under the same honesty rule the git-ref sibling
 * ({@link ./git-ref-links.ts}) follows: a token is only linked when its shape
 * says what it is. A short hex word (`deadbeef`) is a gist id only when a gist
 * URL says so, never on its own.
 *
 * Pure string work, no React and no network, so it stays unit-testable as data.
 */

/** A gist named by a command, as much of it as the text actually said. */
export interface GistRef {
  /** The gist's id, lowercased — how GitHub keys the gist. */
  id: string;
  /** The owner, when an `owner/id` spelling or a URL named one. */
  owner: string | null;
}

/**
 * A gist id standing on its own. Modern ids are 32 hex characters and the
 * oldest are 20; requiring that length is what keeps an ordinary hex word
 * (`deadbeef`, a short sha) from being claimed as a gist.
 */
const GIST_ID = /^[0-9a-f]{20,32}$/i;

/**
 * A gist id inside a gist URL. The URL has already said what the path names, so
 * a shorter id is believable there — but not a full commit sha (40), which is
 * how a `…/raw/<sha>/<file>` segment stays out of the running.
 */
const URL_GIST_ID = /^[0-9a-f]{7,32}$/i;

/** A GitHub login: alphanumeric with inner hyphens, 39 characters at most. */
const OWNER = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/;

/** Hosts that serve gists directly, rather than under a `/gist` prefix. */
const GIST_HOSTS = new Set(['gist.github.com', 'gist.githubusercontent.com']);

/**
 * The path segment holding the id, preferring a full-length one: an owner can
 * itself be hex (`deadbeef/89d7abf…`), so the long segment wins the tie-break
 * and the short-id rule only applies when nothing longer is in the path.
 */
function gistIdIndex(segments: string[]): number {
  const strong = segments.findIndex((segment) => GIST_ID.test(segment));
  return strong >= 0 ? strong : segments.findIndex((segment) => URL_GIST_ID.test(segment));
}

function fromSegments(segments: string[]): GistRef | null {
  const index = gistIdIndex(segments);
  if (index < 0) return null;
  const previous = index > 0 ? segments[index - 1] : null;
  return {
    id: segments[index].toLowerCase(),
    owner: previous && OWNER.test(previous) ? previous : null,
  };
}

function fromUrl(value: string): GistRef | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const segments = url.pathname.split('/').filter(Boolean);
  const host = url.hostname.toLowerCase();
  if (GIST_HOSTS.has(host)) return fromSegments(segments);
  // GitHub Enterprise Server serves gists under `/gist` on the main host.
  if (segments[0] === 'gist') return fromSegments(segments.slice(1));
  return null;
}

/**
 * Parse a `gist`-kind chip's text into the gist it names, or null for text that
 * names nothing linkable (`a gist`, a filename, a truncated id) — callers should
 * treat null as "keep the plain chip".
 */
export function parseGistSubject(text: string): GistRef | null {
  const value = text.trim();
  // A clipped subject is a prefix of the real one; linking it would guess.
  if (value.length === 0 || value.endsWith('…')) return null;
  if (/^https?:\/\//i.test(value)) return fromUrl(value);
  const parts = value.split('/');
  if (parts.length === 2 && OWNER.test(parts[0]) && GIST_ID.test(parts[1])) {
    return { id: parts[1].toLowerCase(), owner: parts[0] };
  }
  return GIST_ID.test(value) ? { id: value.toLowerCase(), owner: null } : null;
}

/**
 * The gist's page on the deployment's GitHub host. github.com serves gists from
 * their own subdomain; GitHub Enterprise Server serves them under `/gist` on the
 * host itself, so an Enterprise deployment's links stay on its own host rather
 * than sending a reader to a public gist that isn't theirs.
 */
export function gistWebUrl(githubWebBaseUrl: string, ref: GistRef): string {
  const base = githubWebBaseUrl.trim().replace(/\/+$/, '');
  const path = ref.owner ? `${ref.owner}/${ref.id}` : ref.id;
  const dotCom = base.replace(/^(https?:\/\/)(?:www\.)?github\.com$/i, '$1gist.github.com');
  return dotCom === base ? `${base}/gist/${path}` : `${dotCom}/${path}`;
}

/** The owner's profile on the same host, for a gist that named one. */
export function gistOwnerUrl(githubWebBaseUrl: string, owner: string): string {
  return `${githubWebBaseUrl.trim().replace(/\/+$/, '')}/${owner}`;
}
